import React, { useEffect } from 'react';

import { AreaChartLoader } from '@rockerbox/styleguide';
import { Loader } from 'semantic-ui-react';

import ChannelOptimization from './ChannelOptimization';
import { DEMO_ACCOUNT, FILTER_FEATURES_BY } from './constants';
import { useMenu } from './hooks/useMenu';
import { useModelData, useModelResponseCurves, useModelFeatures } from './hooks/useModelData';
import { useModelRuns } from './hooks/useModelRuns';
import { usePredictedRevenue } from './hooks/usePredictedRevenue';
import MarketingPerformance from './MarketingPerformance';
import ModelInfo from './ModelInfo';
import ModelRefresh from './ModelRefresh';
import { MenuWrapper } from './parts/stateless';
import WaterfallComparison from './WaterfallComparison';
import { track } from '../../../utils/tracking';
import { forecastViewAtom, useAtomViaDropdown } from '../../atoms';
import { StyledDropdown } from '../../components';
import { TreeTableLoader } from '../../components/loaders';
import { FeatureAliasesProvider } from '../../context/MMMFeatureAliases';
import { useGlobalState } from '../../hooks/global';

const ChosenView = ({ selectedItem, rawData, responseCurves, trainingInfo, seasonality, predRevenueByDate }) => {
  useEffect(() => {
    track(`mmm.${selectedItem.toLowerCase().replaceAll(' ', '_')}.view`);
  }, [selectedItem]);

  switch (selectedItem) {
    case 'Channel Overview':
      return <ChannelOptimization {...{ rawData, trainingInfo, responseCurves, predRevenueByDate }} />;
    case 'Marketing Performance':
      return <MarketingPerformance {...{ rawData, trainingInfo, responseCurves }} />;
    case 'Compare Performance':
      return <WaterfallComparison {...{ rawData, trainingInfo, responseCurves, seasonality }} />;
    case 'Model Details':
      return <ModelInfo {...{ rawData, trainingInfo }} />;
    case 'Build Training Data':
      return <ModelRefresh />;
    default:
      return null;
  }
};

const ChosenLoader = ({ selectedItem }) => {
  switch (selectedItem) {
    case 'Channel Overview':
      return <AreaChartLoader />;
    case 'Marketing Performance':
      return <TreeTableLoader />;
    case 'Compare Performance':
      return <Loader active />;
    case 'Model Details':
      return <AreaChartLoader />;
    case 'Build Training Data':
      return <Loader active />;
    default:
      return <TreeTableLoader />;
  }
};

const MMMView = () => {
  const { user, account } = useGlobalState();
  const { pixel_source_name } = account;

  const { selectedExecDt, setSelectedExecDt, modelOptions, modelRuns } = useModelRuns();
  const { rawData, trainingInfo, seasonality } = useModelData(selectedExecDt, modelRuns);
  const { responseCurves, loading } = useModelResponseCurves(rawData);
  const { features } = useModelFeatures(rawData, FILTER_FEATURES_BY);

  const { predRevenueByDate } = usePredictedRevenue(rawData, trainingInfo?.trainingData, responseCurves, features);

  const items = [
    'Marketing Performance',
    'Compare Performance',
    'Channel Overview',
    'Model Details',
  ];

  const isAdminUser = (user.username || '').includes('@rockerbox.com');
  const isDemoAccount = pixel_source_name === DEMO_ACCOUNT;
  if (isAdminUser && !isDemoAccount) items.push('Build Training Data');

  const { Component: ModelEvaluationMenu, selectedItem } = useMenu(items, items[0], forecastViewAtom, useAtomViaDropdown, true);

  const onModelChange = (e, { value }) => {
    setSelectedExecDt(value);
    track('mmm.marketing_mix_modeling.select_model', { execDt: value });
  };

  return (
    <div className="mmm-view">
      <MenuWrapper>
        <StyledDropdown
          label="Model"
          options={modelOptions}
          value={selectedExecDt}
          onChange={onModelChange}
        />
        <ModelEvaluationMenu />
      </MenuWrapper>
      <div className="right-content" style={{ flex: 7, maxWidth: '85%' }}>
        {loading ? <ChosenLoader {...{ selectedItem }} />
          : <ChosenView {...{ selectedItem, rawData, responseCurves, trainingInfo, seasonality, predRevenueByDate }} />}
      </div>
    </div>
  );
};

export default () => (
  <FeatureAliasesProvider>
    <MMMView />
  </FeatureAliasesProvider>
);
